import { ReactNode } from 'react';

import { cn } from '@/lib/utils';
import { formatNumber } from '../admin/format';

export interface DistributionSegment {
  key: string;
  label: ReactNode;
  value: number;
  /**
   * Colour only when the segment means something (lost = bad, won = good);
   * otherwise the segments step down an ink ramp in order.
   */
  tone?: 'good' | 'bad' | 'watch';
}

/** Ink, stepping lighter — the order of the segments is the order of importance. */
const RAMP = ['bg-foreground', 'bg-foreground/70', 'bg-foreground/50', 'bg-foreground/35', 'bg-foreground/20', 'bg-foreground/10', 'bg-foreground/5'];
const TONE = { good: 'bg-success', bad: 'bg-destructive', watch: 'bg-warning' } as const;

/**
 * One whole, split: where the rankings sit, which devices visit, which
 * questions go unanswered. A single bar reads faster than a pie, and the
 * legend under it carries the exact numbers.
 */
export function DistributionBar({
  segments,
  format = (v) => formatNumber(v),
  showPercent = true,
  columns = 3,
  className,
  testId,
}: {
  segments: DistributionSegment[];
  format?: (value: number) => ReactNode;
  showPercent?: boolean;
  /** Legend columns on wide screens. */
  columns?: 2 | 3 | 4 | 6;
  className?: string;
  testId?: string;
}) {
  const total = segments.reduce((s, x) => s + Math.max(0, x.value), 0);
  const colour = (s: DistributionSegment, i: number) => (s.tone ? TONE[s.tone] : RAMP[Math.min(i, RAMP.length - 1)]);
  const grid = { 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3', 4: 'sm:grid-cols-4', 6: 'sm:grid-cols-3 lg:grid-cols-6' }[columns];

  return (
    <div className={cn('flex flex-col gap-4', className)} data-testid={testId}>
      <div className="flex h-2.5 w-full gap-0.5 overflow-hidden rounded-full bg-muted" role="img" aria-hidden>
        {total > 0 &&
          segments.map((s, i) =>
            s.value > 0 ? <div key={s.key} className={cn('h-full', colour(s, i))} style={{ width: `${(s.value / total) * 100}%` }} /> : null,
          )}
      </div>
      <dl className={cn('grid grid-cols-2 gap-x-6 gap-y-3', grid)}>
        {segments.map((s, i) => (
          <div key={s.key} className="min-w-0">
            <dt className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className={cn('size-2 shrink-0 rounded-[3px]', colour(s, i))} aria-hidden />
              <span className="truncate">{s.label}</span>
            </dt>
            <dd className="mt-0.5 flex items-baseline gap-1.5 tabular-nums">
              <span className="text-sm font-medium">{format(s.value)}</span>
              {showPercent && total > 0 && <span className="text-xs text-muted-foreground">{Math.round((s.value / total) * 100)}%</span>}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
